import { alpha } from '@mui/system';
import * as yup from 'yup';
import {
    noSpaceAllowedInSchemaTest,
    SmartContract,
    themeObject,
} from '../../../../lib';

export interface ExecuteSmartContractArgs {
    key: string;
    value: string;
}
export interface ExecuteSmartContractJobFormInputs {
    chain: string;
    smartContract: SmartContract | null;
    func: string;
    args?: ExecuteSmartContractArgs[];
}

export const ExecuteSmartContractJobSchema = yup.object({
    chain: yup.string().required(),
    smartContract: yup.object().required(),
    func: noSpaceAllowedInSchemaTest(
        yup.string().required(),
        'Enter a valid function.'
    ),
    args: yup.array().of(yup.object()).optional(),
});

export const executeSmartContractJobDefaults = {
    chain: '',
    sdoId: undefined,
    func: '',
    args: [],
    smartContract: null,
};

export const executeContractContainerStyles = {
    executeContractContainer: {
        mt: 3,
        mx: '82.5px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': {
            display: 'none',
        },
        '&::-webkit-scrollbar-thumb': {
            background: themeObject.palette.primary[500],
            borderRadius: '100px',
        },
    },
};

export const executeSmartContractJobStyles = {
    root: {
        '& .MuiBackdrop-root': {
            backgroundColor: alpha(themeObject.palette.grayScale[50], 0.75),
        },
        '& .MuiDialog-container': {
            '& .MuiPaper-root': {
                height: '100vh',
                width: '1186px',
                maxWidth: 'none',
                maxHeight: 'none',
                boxShadow: 'none',
            },
        },
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': {
            display: 'none',
        },
        '&::-webkit-scrollbar-thumb': {
            background: themeObject.palette.primary[500],
            borderRadius: '100px',
        },
    },
    mainContainer: {
        mt: 1,
        display: 'flex',
        flexDirection: 'column',
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': {
            display: 'none',
        },
        '&::-webkit-scrollbar-thumb': {
            background: themeObject.palette.primary[500],
            borderRadius: '100px',
        },
    },
    stepperContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        mt: 2,
    },
    stepper: {
        width: '300px',
    },
    basicInfoFormContainer: {
        mx: '143.5px',
        mt: 1,
    },
    minimizedExecuteContractContainer: {
        ...executeContractContainerStyles.executeContractContainer,
        flexWrap: 'wrap',
    },
    basicInfoFormItemContainer: {
        width: '100%',
        mt: 2,
    },
    functionDefinitionItemContainer: {
        width: '100%',
    },
    functionDefinitionLabelContainer: {
        width: '100%',
        mt: 2,
    },
    functionDefinitionItemInputContainer: {
        display: 'flex',
        flexDirection: 'column',
        mt: 2,
    },
    smartContractFormSectionContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        pr: 7.5,
    },
    codeMirrorLabel: {
        fontFamily: 'Red Hat Text',
        fontWeight: '600',
        fontSize: '16px',
        lineHeight: '21.17px',
        letter: '0.15px',
        color: themeObject.palette.grayScale[800],
    },
    codeMirrorWidth: '456px',
    codeMirrorHeight: '541px',
    executeContractSectionLabel: {
        fontFamily: 'Raleway',
        fontSize: '20px',
        fontWeight: 500,
        lineHeight: '23.48x',
        color: themeObject.palette.primary[700],
    },
    titleContainer: {
        width: '100%',
        mb: 2,
    },
    functionWidgetContainer: {
        width: '100%',
        mt: 3,
    },
    functionWidgetEmptyStateItemContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
    },
    headingTextContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        mt: 1.5,
    },
    emptyStateBodyTextContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        mt: 2,
    },
    functionWidgetEmptyStateContainer: {
        width: '435px',
        height: '148',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        mt: 4,
        mx: 4,
    },
    functionWidgetIcon: {
        width: '105.39',
        height: '63.39',
    },
    functionWidgetIconBodyText: {
        fontFamily: 'Red Hat Text',
        fontWeight: '400',
        fontSize: '16px',
        lineHeight: '21.17px',
        color: themeObject.palette.grayScale[800],
    },
    functionParamsContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
    },
    functionParameterInput: {
        width: '230px',
    },
    functionValueInput: {
        width: '230px',
    },
    functionParamsDeleteButtonContainer: {
        height: '40px',
        width: '20px',
        mt: 4,
        ml: 1.5,
    },
    functionParamsDeleteButton: {
        height: '20px',
        width: '20px',
        backgroundColor: themeObject.palette.grayScale[500],
        color: themeObject.palette.background.default,
    },
    addParameterButtonContainer: {
        width: '100%',
    },
    addParameterButton: {
        borderRadius: '20px',
        width: '150px',
        height: '40.25px',
        '& .MuiTypography-root': {
            fontFamily: 'Red Hat Text',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '24px',
            color: themeObject.palette.grayScale[700],
            ml: '2px',
        },
    },
    executeButtonContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        pt: 3,
    },
};

export const executeSmartContractSteps: any[] = [
    {
        label: 'Basic Info',
        state: 'active',
    },
    {
        label: 'Contract',
        state: 'complete',
    },
];
